/*
 * IMPECCABLE DECISION RECORD (surface-scope revamp, seed key c74c5620, candidate 5/7)
 * THESIS: a Sentry-guided task is a conversation with stops, not one long form —
 *   replace the single-scroll dossier with a real 4-step wizard (What / Who /
 *   Reach / Goal) that only ever asks for what the current step needs.
 * OWN-WORLD: inherits DESIGN.md verbatim (Signal Pink primary action, Synaptic
 *   Violet structural accent, light page + white rounded-2xl/3xl cards, Inter,
 *   spring motion). No palette, type, or material change — structure only.
 * STORY: the visitor picks what they need help with, tells Sentry who they are,
 *   how to reach them, and what success looks like — each step confirmed before
 *   the next opens, with the trail visible in a progress rail and a final recap
 *   before sending.
 * FIRST VIEWPORT: Sentry's guidance line at top (unchanged avatar block), a
 *   4-node progress rail beneath it, then the current step's fields; Back/Next
 *   pinned at the card's bottom edge.
 * FORM: candidate 5 of 7 grounded structures for this task (step-tracker
 *   wizard), assigned by concept-seed.mjs --scope surface --mode operate.
 * FINISH: unreviewed and undocumented is unfinished; this build ends with the
 *   finish review, the verdict, and DESIGN.md.
 */
import React, { useState, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  Loader2,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  Pencil,
  Globe,
  Smartphone,
  Monitor,
  Network,
  BrainCircuit,
  Workflow,
  Database,
  KeyRound,
  Sparkles,
  Mail,
  Phone,
  Send,
} from "lucide-react";
import Navbar from "../Navbar";
import Footer from "../Footer";
import { useUser } from "../../contexts/UserContext";
import getStartedService from "../../services/getStartedService";
import MiniSentry from "../chat/MiniSentry";
import AuthPanel from "./AuthPanel";
import { trackTrigger } from "../../utils/truConversion";

const SERVICE_TYPES = [
  { label: "Portal / Website", icon: Globe },
  { label: "Web or Mobile App", icon: Smartphone },
  { label: "Desktop Application", icon: Monitor },
  { label: "Systems Integration", icon: Network },
  { label: "Neural Network / Custom Model", icon: BrainCircuit },
  { label: "AI Workflow & Agents", icon: Workflow },
  { label: "Data Pipeline", icon: Database },
  { label: "Licenses", icon: KeyRound },
  { label: "Something else", icon: Sparkles },
];

const ROLES = [
  "Founder / CEO",
  "CTO / Engineering Lead",
  "Product Director",
  "Operations Manager",
  "Decision Maker",
  "Developer / Architect",
  "Other",
];

const STEP_LABELS = ["What", "Who", "Reach", "Goal"];

const inputCls =
  "w-full bg-white border border-gray-200 rounded-2xl px-4 h-14 text-sm text-gray-800 placeholder:text-gray-400 outline-none focus:border-[#ea3d95] focus:ring-4 focus:ring-[#ea3d95]/10 transition-all";

const fieldLabelCls =
  "ml-1 block text-xs font-semibold uppercase tracking-wider text-gray-500";

const cardCls =
  "relative overflow-hidden rounded-3xl bg-white shadow-xl shadow-gray-200/70 ring-1 ring-gray-100";

const stepVariants = {
  enter: (dir) => ({ x: dir >= 0 ? 32 : -32, opacity: 0 }),
  center: { x: 0, opacity: 1, transition: { duration: 0.32, ease: [0.16, 1, 0.3, 1] } },
  exit: (dir) => ({
    x: dir >= 0 ? -32 : 32,
    opacity: 0,
    transition: { duration: 0.18, ease: [0.7, 0, 0.84, 0] },
  }),
};

const Shell = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-gray-50 font-sans relative overflow-x-hidden">
    <div className="fixed w-full z-[101]">
      <Navbar />
    </div>
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <div className="absolute -top-32 right-0 h-[420px] w-[520px] rounded-full bg-[#5742bb]/[0.07] blur-3xl" />
      <div className="absolute top-1/3 -left-32 h-[360px] w-[360px] rounded-full bg-[#ea3d95]/[0.07] blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-[300px] w-[420px] rounded-full bg-[#5742bb]/[0.04] blur-3xl" />
    </div>
    <main className="relative flex flex-1 items-center justify-center px-6 pt-32 pb-16">
      <div className="w-full max-w-3xl">{children}</div>
    </main>
    <Suspense fallback={<div className="h-64 bg-[#111113]" />}>
      <Footer />
    </Suspense>
  </div>
);

// Progress rail: a chain of decision points, always visible, always tells you
// where you are and lets you jump back to anything already answered.
const ProgressRail = ({ step, furthestStep, onJump }) => (
  <div className="mt-8 flex items-center">
    {STEP_LABELS.map((label, i) => {
      const isDone = i < step;
      const isActive = i === step;
      const isReachable = i <= furthestStep;
      return (
        <React.Fragment key={label}>
          <button
            type="button"
            disabled={!isReachable || isActive}
            onClick={() => isReachable && onJump(i)}
            className="flex flex-col items-center gap-2 disabled:cursor-default"
          >
            <span
              className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold transition-colors ${
                isActive
                  ? "bg-[#ea3d95] text-white"
                  : isDone
                    ? "bg-[#5742bb] text-white"
                    : "bg-gray-100 text-gray-400"
              }`}
            >
              {isDone ? <Check size={14} strokeWidth={3} /> : i + 1}
            </span>
            <span
              className={`text-xs font-semibold uppercase tracking-wider ${
                isActive ? "text-[#ea3d95]" : isDone ? "text-[#5742bb]" : "text-gray-400"
              }`}
            >
              {label}
            </span>
          </button>
          {i < STEP_LABELS.length - 1 && (
            <span
              className={`mx-2 mb-5 h-px flex-1 ${i < step ? "bg-[#5742bb]" : "bg-gray-200"}`}
            />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const ServicesFlow = () => {
  const navigate = useNavigate();
  const { user, userData } = useUser();

  const [step, setStep] = useState(0);
  const [furthestStep, setFurthestStep] = useState(0);
  const [direction, setDirection] = useState(1);

  const [serviceTypes, setServiceTypes] = useState([]);
  const [organization, setOrganization] = useState("");
  const [role, setRole] = useState("");
  const [phone, setPhone] = useState("");
  const [preferredContact, setPreferredContact] = useState("email"); // email | phone
  const [purpose, setPurpose] = useState("");
  const [roleOpen, setRoleOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errors, setErrors] = useState({});

  const displayName =
    user?.displayName || userData?.displayName || user?.email?.split("@")[0];
  const firstName = (displayName || "").trim().split(/\s+/)[0];

  const toggleType = (label) =>
    setServiceTypes((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label],
    );

  const validateStep = (s) => {
    const e = {};
    if (s === 0 && serviceTypes.length === 0) e.serviceTypes = true;
    if (s === 1) {
      if (!organization || organization.trim().length < 2) e.organization = true;
      if (!role) e.role = true;
    }
    if (s === 3 && (!purpose || purpose.trim().length < 10)) e.purpose = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const goToStep = (target) => {
    setDirection(target >= step ? 1 : -1);
    setStep(target);
  };

  const handleNext = () => {
    if (!validateStep(step)) return;
    const target = Math.min(step + 1, STEP_LABELS.length - 1);
    setFurthestStep((f) => Math.max(f, target));
    goToStep(target);
  };

  const handleBack = () => goToStep(Math.max(step - 1, 0));

  const handleSubmit = async () => {
    if (!validateStep(0) || !validateStep(1) || !validateStep(3)) return;
    setStatus("submitting");
    try {
      await getStartedService.submitServiceInquiry({
        user,
        serviceTypes,
        organization,
        role,
        phone,
        preferredContact,
        purpose,
      });
      setStatus("success");
      trackTrigger("nw-services-inquiry-submitted");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  // ── Auth gate ──────────────────────────────────────────────────────────────
  if (!user) {
    return (
      <Shell>
        <div className={`${cardCls} p-8 sm:p-10`}>
          <AuthPanel
            title="Let's get you in touch"
            subtitle="Register or sign in so we can route your request and follow up."
          />
        </div>
      </Shell>
    );
  }

  // ── Success ─────────────────────────────────────────────────────────────────
  if (status === "success") {
    return (
      <Shell>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className={`${cardCls} p-8 sm:p-10`}
        >
          <div className="grid grid-cols-1 gap-8 md:grid-cols-5">
            <div className="flex flex-col items-start text-left md:col-span-2">
              <motion.div
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.1 }}
                className="mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#ea3d95] to-[#5742bb] text-white shadow-lg shadow-[#ea3d95]/20"
              >
                <Check size={24} />
              </motion.div>
              <h2 className="text-2xl font-bold text-gray-900">Request received</h2>
              <p className="mt-3 text-gray-600">
                Thanks, {firstName}. Our team has your brief and will get back to you at{" "}
                <span className="font-medium text-gray-800">{user.email}</span> shortly.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => navigate("/appcenter-store")}
                  className="rounded-xl bg-brandPrimary px-6 py-3 text-sm font-semibold text-white shadow-md transition-colors hover:bg-brandAccent"
                >
                  Explore the App Center
                </button>
                <button
                  onClick={() => navigate("/")}
                  className="rounded-xl border border-gray-200 px-6 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Back to home
                </button>
              </div>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-6 text-left md:col-span-3">
              <span className="mb-4 block text-xs font-semibold uppercase tracking-wider text-gray-500">
                Your brief
              </span>
              <div className="flex flex-wrap gap-1.5">
                {serviceTypes.map((t) => (
                  <span
                    key={t}
                    className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700"
                  >
                    {t}
                  </span>
                ))}
              </div>
              <div className="mt-4 border-t border-gray-200 pt-4 text-sm text-gray-600">
                <span className="font-medium text-gray-800">{organization}</span>
                {role ? ` · ${role}` : ""}
              </div>
              {purpose && (
                <p className="mt-3 text-sm leading-relaxed text-gray-600">{purpose}</p>
              )}
            </div>
          </div>
        </motion.div>
      </Shell>
    );
  }

  const sentryLine = [
    <>
      Hi {firstName} — tell me a little about what you're building and I'll
      connect you with the right people on our team.
    </>,
    <>Got it. Now, who am I connecting this to?</>,
    <>Perfect — where should the team follow up with you?</>,
    <>Last thing — what does success look like here?</>,
  ][step];

  return (
    <Shell>
      <motion.button
        onClick={() => navigate("/get-started")}
        className="mb-6 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800"
        whileHover={{ x: -3 }}
      >
        <ArrowLeft size={16} /> Back to options
      </motion.button>

      <div className={cardCls}>
        <div className="p-6 sm:p-10">
          {/* Sentry — avatar/label block unchanged; only the spoken line advances with the step */}
          <div className="flex items-start gap-4 rounded-2xl border border-gray-100 bg-gray-50/70 p-5">
            <div className="flex shrink-0 flex-col items-center gap-1.5">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 ring-1 ring-gray-200">
                <MiniSentry active />
              </div>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-[#5742bb]">
                Sentry
              </span>
            </div>
            <AnimatePresence mode="wait">
              <motion.p
                key={step}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="pt-1 text-gray-700 leading-relaxed"
              >
                {sentryLine}
              </motion.p>
            </AnimatePresence>
          </div>

          <ProgressRail step={step} furthestStep={furthestStep} onJump={goToStep} />

          <div className="relative mt-9 min-h-[260px] overflow-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={step}
                custom={direction}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                {step === 0 && (
                  <div>
                    <label className="mb-4 block text-sm font-semibold text-gray-700">
                      How can we help? <span className="text-[#ea3d95]">*</span>{" "}
                      <span className="font-normal text-gray-400">
                        (pick all that apply)
                      </span>
                    </label>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {SERVICE_TYPES.map(({ label, icon: Icon }) => {
                        const active = serviceTypes.includes(label);
                        return (
                          <button
                            key={label}
                            type="button"
                            onClick={() => toggleType(label)}
                            className={`group relative flex flex-col items-start gap-2.5 rounded-2xl border p-4 text-left transition-all ${
                              active
                                ? "border-[#ea3d95] bg-[#ea3d95]/[0.06] shadow-sm shadow-[#ea3d95]/10"
                                : "border-gray-200 bg-white hover:border-[#ea3d95] hover:bg-[#ea3d95]/[0.06]"
                            }`}
                          >
                            <span
                              className={`flex h-9 w-9 items-center justify-center rounded-xl transition-colors ${
                                active
                                  ? "bg-[#ea3d95] text-white"
                                  : "bg-gray-100 text-gray-500 group-hover:bg-[#ea3d95] group-hover:text-white"
                              }`}
                            >
                              <Icon size={17} />
                            </span>
                            <span
                              className={`text-sm font-medium leading-snug ${
                                active ? "text-[#ea3d95]" : "text-gray-700 group-hover:text-[#ea3d95]"
                              }`}
                            >
                              {label}
                            </span>
                            {active && (
                              <span className="absolute right-3 top-3 flex h-4 w-4 items-center justify-center rounded-full bg-[#ea3d95] text-white">
                                <Check size={10} strokeWidth={3} />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    {errors.serviceTypes && (
                      <p className="mt-2 text-xs text-red-500">Select at least one.</p>
                    )}
                  </div>
                )}

                {step === 1 && (
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <label className={fieldLabelCls}>
                        Organization <span className="text-[#ea3d95]">*</span>
                      </label>
                      <input
                        value={organization}
                        onChange={(e) => {
                          setOrganization(e.target.value);
                          if (errors.organization) setErrors({ ...errors, organization: false });
                        }}
                        placeholder="Your company"
                        maxLength={120}
                        autoFocus
                        className={`${inputCls} ${errors.organization ? "border-red-400" : ""}`}
                      />
                    </div>

                    <div className="relative space-y-1.5">
                      <label className={fieldLabelCls}>
                        Your role <span className="text-[#ea3d95]">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setRoleOpen((v) => !v)}
                        className={`${inputCls} flex items-center justify-between text-left ${errors.role ? "border-red-400" : ""}`}
                      >
                        <span className={role ? "text-gray-800" : "text-gray-400"}>
                          {role || "Select role"}
                        </span>
                        <ChevronDown
                          size={16}
                          className={`text-gray-400 transition-transform ${roleOpen ? "rotate-180" : ""}`}
                        />
                      </button>
                      {roleOpen && (
                        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 overflow-hidden rounded-2xl border border-gray-200 bg-white py-2 shadow-xl">
                          {ROLES.map((r) => (
                            <button
                              key={r}
                              type="button"
                              onClick={() => {
                                setRole(r);
                                setRoleOpen(false);
                                if (errors.role) setErrors({ ...errors, role: false });
                              }}
                              className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-[#ea3d95]/5 hover:text-[#ea3d95]"
                            >
                              {r}
                              {role === r && <Check size={14} className="text-[#ea3d95]" />}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <div className="space-y-1.5">
                      <label className={fieldLabelCls}>
                        Phone{" "}
                        <span className="font-normal normal-case tracking-normal text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+1 555 000 0000"
                        maxLength={40}
                        autoFocus
                        className={inputCls}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className={fieldLabelCls}>Preferred contact</label>
                      <div className="flex h-14 items-center gap-2">
                        {[
                          { value: "email", label: "Email", Icon: Mail },
                          { value: "phone", label: "Phone", Icon: Phone },
                        ].map((opt) => {
                          const active = preferredContact === opt.value;
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => setPreferredContact(opt.value)}
                              className={`flex flex-1 h-full items-center justify-center gap-2 rounded-2xl border text-sm font-medium transition-colors ${
                                active
                                  ? "border-[#ea3d95] bg-[#ea3d95]/[0.06] text-[#ea3d95]"
                                  : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:text-gray-800"
                              }`}
                            >
                              <opt.Icon size={15} />
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-8">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label className={fieldLabelCls}>
                          What are you trying to achieve? <span className="text-[#ea3d95]">*</span>
                        </label>
                        <span className="text-xs text-gray-400">{purpose.length}/2000</span>
                      </div>
                      <textarea
                        value={purpose}
                        onChange={(e) => {
                          setPurpose(e.target.value);
                          if (errors.purpose) setErrors({ ...errors, purpose: false });
                        }}
                        rows={4}
                        maxLength={2000}
                        autoFocus
                        placeholder="A sentence or two about the outcome you're after."
                        className={`w-full resize-none rounded-2xl border bg-white px-4 py-3.5 text-sm text-gray-800 placeholder:text-gray-400 outline-none transition-all focus:border-[#ea3d95] focus:ring-4 focus:ring-[#ea3d95]/10 ${
                          errors.purpose ? "border-red-400" : "border-gray-200"
                        }`}
                      />
                    </div>

                    {/* Recap — every earlier answer, editable via jump-back */}
                    <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-5">
                      <div className="mb-3 flex items-center justify-between">
                        <span className={fieldLabelCls}>Sending to Neuralward</span>
                        <button
                          type="button"
                          onClick={() => goToStep(0)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#5742bb] hover:text-[#4835a0]"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {serviceTypes.map((t) => (
                          <span
                            key={t}
                            className="rounded-full bg-white border border-gray-200 px-3 py-1 text-xs font-medium text-gray-700"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-4 text-sm">
                        <span className="text-gray-600">
                          <span className="font-medium text-gray-800">{organization}</span>
                          {role ? ` · ${role}` : ""}
                        </span>
                        <button
                          type="button"
                          onClick={() => goToStep(1)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#5742bb] hover:text-[#4835a0]"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                        <span className="text-gray-600">
                          {preferredContact === "phone" && phone.trim() ? (
                            <>Reach by phone at <span className="font-medium text-gray-800">{phone}</span></>
                          ) : (
                            <>Reply to <span className="font-medium text-gray-800">{user.email}</span></>
                          )}
                        </span>
                        <button
                          type="button"
                          onClick={() => goToStep(2)}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-[#5742bb] hover:text-[#4835a0]"
                        >
                          <Pencil size={11} /> Edit
                        </button>
                      </div>
                    </div>

                    {status === "error" && (
                      <p className="text-sm text-red-500">
                        Something went wrong sending your request. Please try again.
                      </p>
                    )}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Back / Next — pinned nav, always in the same place regardless of step content height */}
          <div className="mt-9 flex items-center justify-between border-t border-gray-100 pt-6">
            {step > 0 ? (
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-2 rounded-2xl border border-gray-200 px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <ArrowLeft size={16} /> Back
              </button>
            ) : (
              <span />
            )}

            {step < STEP_LABELS.length - 1 ? (
              <motion.button
                type="button"
                onClick={handleNext}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="inline-flex items-center gap-2 rounded-2xl bg-brandPrimary px-6 py-3 text-sm font-semibold text-white shadow-md shadow-[#ea3d95]/20 transition-colors hover:bg-brandAccent"
              >
                Next <ArrowRight size={16} />
              </motion.button>
            ) : (
              <motion.button
                type="button"
                onClick={handleSubmit}
                disabled={status === "submitting"}
                whileHover={{ scale: status === "submitting" ? 1 : 1.01 }}
                whileTap={{ scale: status === "submitting" ? 1 : 0.99 }}
                className="inline-flex items-center gap-2 rounded-2xl bg-brandPrimary px-6 py-3 text-sm font-semibold text-white shadow-md shadow-[#ea3d95]/20 transition-colors hover:bg-brandAccent disabled:opacity-60"
              >
                {status === "submitting" ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send size={16} /> Send to Neuralward
                  </>
                )}
              </motion.button>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
};

export default ServicesFlow;
